/**
 * The taal (rhythmic cycle) grammar.
 *
 * A taal is described by two parallel strings:
 *
 *   detail  one character per matra (beat), from {x, t, k} in either case
 *             x = ordinary beat
 *             t = taali (clap)
 *             k = khaali (wave)
 *           An UPPERCASE letter marks the FIRST beat of a new khand (vibhag),
 *           which is what draws the divider in the grid.
 *
 *   render  comma-separated bol for each matra; must have exactly
 *           `detail.length` entries.
 *
 * The first beat is the sam.
 *
 * This module is pure and framework-free: it returns plain descriptors
 * (`isKhandStart`) rather than styles, so the same parse feeds the on-screen
 * grid, the admin editor preview, and eventually server-side PDF rendering.
 */

export interface TaalInput {
    id: string;
    name: string;
    detail: string;
    render: string;
}

export interface MatraInfo {
    /** Zero-based beat index. */
    index: number;
    /** The raw grammar character for this beat. */
    char: string;
    /** True when this beat begins a new khand (uppercase in `detail`). */
    isKhandStart: boolean;
    /** Marker shown under the beat: "X" (sam), "O" (khaali), a clap number, or "". */
    marker: string;
    /** The bol for this beat; empty when `render` does not cover it. */
    bol: string;
    /** Human-readable beat type, useful for tooltips. */
    kind: "sam" | "sam-khaali" | "taali" | "khaali" | "beat";
}

export interface ParsedTaal {
    id: string;
    name: string;
    matraCount: number;
    matras: MatraInfo[];
    /**
     * False when `render` did not have one entry per matra.
     *
     * The previous implementation THREW in this case — from inside a render
     * path, which took down the whole editor for a malformed taal document.
     * Parsing is tolerant instead: missing bols come back as empty strings and
     * the caller can surface the inconsistency where it can actually be fixed.
     */
    isConsistent: boolean;
}

const isUpperCase = (char: string): boolean => char >= "A" && char <= "Z";

/**
 * The taal used when a file opts out of taal rendering entirely. Its id is
 * checked so no markers are drawn.
 */
export const IGNORE_RENDER_TAAL_ID = "000";

function markerFor(index: number, char: string, taalId: string): string {
    if (taalId === IGNORE_RENDER_TAAL_ID) return "";
    if (index === 0) return char.toLowerCase() === "k" ? "O" : "X";
    if (char === "K") return "O";
    if (char === "T") return String(index + 1);
    return "";
}

function kindFor(index: number, char: string): MatraInfo["kind"] {
    const lower = char.toLowerCase();
    if (index === 0) return lower === "k" ? "sam-khaali" : "sam";
    if (lower === "t") return "taali";
    if (lower === "k") return "khaali";
    return "beat";
}

/** Parses a taal into per-matra descriptors. Pure; never throws. */
export function parseTaal(taal: TaalInput): ParsedTaal {
    const chars = taal.detail.split("");
    const bols = taal.render === "" ? [] : taal.render.split(",");

    return {
        id: taal.id,
        name: taal.name,
        matraCount: chars.length,
        isConsistent: bols.length === chars.length,
        matras: chars.map((char, index) => ({
            index,
            char,
            isKhandStart: isUpperCase(char),
            marker: markerFor(index, char, taal.id),
            bol: bols[index] ?? "",
            kind: kindFor(index, char),
        })),
    };
}
